/**
 * Capture → shareable photo, entirely on the phone (WS-20; generalized out of the
 * check-in module's original `outbox/image.ts` (WS-13), parameterized by max dimension and
 * JPEG quality instead of hardcoding check-in's constants, so recipes' header-photo upload
 * reuses the exact same pipeline instead of forking it).
 *
 * Three things happen here, and the order matters:
 *
 * 1. **Downscale** to a longest-side cap. A modern phone camera produces 4–12 MB per shot;
 *    over a household's actual networks that is the difference between a photo that
 *    arrives and one that doesn't.
 * 2. **Re-encode** as JPEG through a canvas. This is also what **strips EXIF**: a canvas
 *    holds pixels and nothing else, so `toBlob` emits a fresh file with no metadata
 *    segments at all — no GPS coordinates, no camera serial, no capture timestamp.
 *    `hasExifSegment()` below is the assertion that keeps that claim true.
 * 3. **Hash** the encoded bytes into the photo id, so the upload is content-addressed and
 *    every retry is the same idempotent PUT.
 *
 * `createImageBitmap` is used rather than an `<img>` element because it honours the EXIF
 * orientation tag while decoding (`imageOrientation: 'from-image'`) — otherwise stripping
 * the metadata would leave every portrait photo lying on its side.
 */

export interface PreparedPhoto {
  id: string;
  blob: Blob;
  contentType: 'image/jpeg';
  width: number;
  height: number;
  /** Object URL for immediate local rendering; revoke when the record leaves the screen. */
  previewUrl: string;
}

export function scaleToFit(
  width: number,
  height: number,
  max: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= max) return { width, height };
  const ratio = max / longest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * True when `bytes` contain no EXIF/XMP metadata segment. A JPEG is a sequence of markers;
 * EXIF rides in APP1 (`0xFFE1`). The canvas re-encode should never produce one — this is
 * the assertion that keeps that true.
 */
export function hasExifSegment(bytes: Uint8Array): boolean {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return false;
  let offset = 2;
  while (offset + 4 <= bytes.length) {
    if (bytes[offset] !== 0xff) return false;
    const marker = bytes[offset + 1] ?? 0;
    // Start of scan: image data from here on, no more metadata markers.
    if (marker === 0xda) return false;
    if (marker === 0xe1) return true;
    const length = ((bytes[offset + 2] ?? 0) << 8) | (bytes[offset + 3] ?? 0);
    if (length < 2) return false;
    offset += 2 + length;
  }
  return false;
}

async function decode(file: Blob): Promise<ImageBitmap> {
  return createImageBitmap(file, { imageOrientation: 'from-image' });
}

function encode(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas encode failed'))),
      'image/jpeg',
      quality,
    );
  });
}

export interface PreparePhotoOptions {
  maxDimension: number;
  quality: number;
}

/** Runs the full pipeline on a camera capture or a library pick. */
export async function preparePhoto(
  file: Blob,
  options: PreparePhotoOptions,
): Promise<PreparedPhoto> {
  const bitmap = await decode(file);
  const { width, height } = scaleToFit(bitmap.width, bitmap.height, options.maxDimension);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('2d canvas unavailable');
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await encode(canvas, options.quality);
  const buffer = await blob.arrayBuffer();
  const id = await sha256Hex(buffer);

  return {
    id,
    blob,
    contentType: 'image/jpeg',
    width,
    height,
    previewUrl: URL.createObjectURL(blob),
  };
}
